import { readPayWarnings } from '../pay-lock';
import { getDB, EntityStore } from './db';
import { flushQueue, cleanupQueue } from './sync-queue';
import { flushPendingUploads } from './upload-sync';
import { flushClockQueue } from './clock-queue';
import { apiClient } from '../../api/client';
import { foldCasingStringLists } from './casing-string-cache';

export interface SyncResult {
  /**
   * `conflicts` counts entries this push wrote to the review log: conflicts
   * PLUS applied mutations that carried a server notice (api#1352,
   * pay_already_paid). Every caller hands it to `setConflicts`, which only
   * re-reads the log when it is above zero. A push that applied with a notice
   * and conflicted on nothing therefore still has to count, or the notice
   * would wait for the next mount to be seen.
   */
  pushed: { applied: number; conflicts: number; errors: number };
  pulled: Record<string, number>;
  /**
   * Client clock, stamped when the sync finished. This is for the "Synced 3m
   * ago" label in `SyncStatusBar` and nothing else — a relative label about
   * local wall-clock time is the one job a local clock is right for.
   *
   * It is NOT the pull cursor. Conflating the two is what defect 3 was: this
   * value was written into `syncMeta.lastSyncAt` and then sent back as
   * `?since=`, so a client whose clock ran fast asked the server for changes
   * after a moment that had not happened yet. See `watermark`.
   */
  timestamp: string;
  /**
   * The server-supplied cursor written to `syncMeta.lastSyncAt`, or null when
   * the cursor was deliberately left where it was.
   */
  watermark: string | null;
}

/**
 * Failure of the pull half of a sync. Thrown so no caller can stamp "Synced
 * just now" over a sync that did not pull.
 */
export interface SyncPullFailedError extends Error {
  __fieldiqSyncPullFailed: true;
  cause?: unknown;
}

export function isSyncPullFailedError(err: unknown): err is SyncPullFailedError {
  return !!err && (err as any).__fieldiqSyncPullFailed === true;
}

function pullFailed(message: string, cause?: unknown): SyncPullFailedError {
  const err = new Error(message) as SyncPullFailedError;
  err.name = 'SyncPullFailedError';
  err.__fieldiqSyncPullFailed = true;
  err.cause = cause;
  return err;
}

const EPOCH = '1970-01-01T00:00:00Z';

/**
 * Hard stop on pagination rounds.
 *
 * The loop was previously `while (hasMore)` with no bound at all. It now
 * advances by the *slowest* entity (see below), which is correct but makes a
 * round strictly cheaper, so a bound matters more. Hitting it is treated as a
 * pull that did not finish — the cursor stays put and the next sync picks up
 * where this one did, rather than silently declaring victory partway through.
 */
const MAX_PULL_ROUNDS = 50;

export async function performSync(): Promise<SyncResult> {
  // Left rejecting on purpose. A sync that pulled nothing into a cache that
  // does not exist has not synced, and both callers depend on knowing that:
  // `SyncInit` in App.tsx and `SyncStatusBar.handleForceSync` would otherwise
  // stamp `lastSyncAt` and show "Synced just now" over stale or absent data.
  //
  // Both callers already `.catch()` this and both clear `isSyncing` in a
  // `finally`, which is the part that matters here — before `getDB` was
  // bounded, a wedged database left `isSyncing` true forever and the blue
  // "Syncing..." bar was itself a permanent spinner.
  const db = await getDB();
  const result: SyncResult = {
    pushed: { applied: 0, conflicts: 0, errors: 0 },
    pulled: {},
    timestamp: new Date().toISOString(),
    watermark: null,
  };

  // 1. Push local changes first
  try {
    const pushResult = await flushQueue();
    result.pushed = {
      applied: pushResult.applied?.length || 0,
      conflicts: (pushResult.conflicts?.length || 0)
        + (pushResult.applied || []).filter((a: any) => readPayWarnings(a).length > 0).length,
      errors: pushResult.errors?.length || 0,
    };
  } catch (err) {
    console.warn('[sync] Push failed, continuing with pull:', err);
  }

  // 1b. Upload queued photos/attachments
  try {
    await flushPendingUploads();
  } catch (err) {
    console.warn('[sync] Upload flush failed:', err);
  }

  // 1c. Queued clock taps (lib/offline/clock-queue.ts). Here because the online
  // detector runs performSync on reconnect: a tap queued on ClockInScreen is
  // sent even after the hand has left that screen. A failure leaves the tap
  // queued and never stops the pull.
  try {
    await flushClockQueue();
  } catch (err) {
    console.warn('[sync] Clock queue flush failed:', err);
  }

  // 2. Pull server changes
  const lastSyncAt: string = (await db.get('syncMeta', 'lastSyncAt')) || EPOCH;

  const entityStoreMap: Record<string, EntityStore> = {
    tickets: 'tickets',
    ticketLineItems: 'ticketLineItems',
    jobs: 'jobs',
    equipment: 'equipment',
    customers: 'customers',
  };

  let since = lastSyncAt;
  let serverTime: string | null = null;
  let pullComplete = false;
  let pullError: unknown = null;
  let rounds = 0;

  while (rounds < MAX_PULL_ROUNDS) {
    rounds += 1;

    let data: any;
    try {
      const response = await apiClient.get('/sync/pull', { params: { since } });
      data = response.data;
    } catch (err) {
      // The single most important line in this file. This `catch` used to set
      // `hasMore = false` and fall through to the cursor write below, so a
      // failed pull *advanced* the cursor and every record the server changed
      // in the gap was never asked for again. Silently, permanently.
      pullError = err;
      break;
    }

    let anyHasMore = false;
    // `updated_at` of the last record of every entity that still has more.
    const frontier: string[] = [];

    for (const [entityKey, store] of Object.entries(entityStoreMap)) {
      const entityData = data[entityKey];
      if (!entityData?.data?.length) continue;

      result.pulled[entityKey] = (result.pulled[entityKey] || 0) + entityData.data.length;

      for (const record of entityData.data) {
        if (record.deleted_at) {
          // Soft-deleted — remove from local store
          try {
            await db.delete(store, record.id);
          } catch {
            // May not exist locally, that's fine
          }
        } else {
          await db.put(store, record);
        }
      }

      if (entityData.hasMore) {
        anyHasMore = true;
        const lastRecord = entityData.data[entityData.data.length - 1];
        if (lastRecord?.updated_at) frontier.push(lastRecord.updated_at);
      }
    }

    // api#830: the strings and per-string metres for exactly the jobs and
    // tickets on this page, onto the rows just stored. See casing-string-cache.
    // A failure here costs only the offline inputs, never the pull.
    try {
      await foldCasingStringLists(db, data);
    } catch (err) {
      console.warn('[sync] Casing string fold skipped:', err);
    }

    if (typeof data.serverTime === 'string' && data.serverTime) {
      serverTime = data.serverTime;
    }

    if (!anyHasMore) {
      pullComplete = true;
      break;
    }

    // ── The shared-cursor bug (audit line 88-95) ──────────────────────────
    //
    // `/sync/pull` takes ONE `since` for all five entity types, so there is
    // exactly one cursor to move and no client-side way to give each entity
    // its own window without changing the endpoint (out of scope here — no
    // API handler changes). What we can fix is which way it moves.
    //
    // It used to take the MAXIMUM `updated_at` across every entity reporting
    // `hasMore`. If tickets paginated to 14:00 while jobs only reached 09:00,
    // the next round asked `since=14:00` — and every job between 09:00 and
    // 14:00 was skipped for good. The fast entity ate the slow entity's
    // window.
    //
    // The MINIMUM is the only safe choice: the slowest entity sets the pace,
    // and no entity is ever asked to skip past records it has not seen. The
    // cost is that faster entities re-receive rows they already have, which
    // `db.put` absorbs idempotently — re-reading a page is cheap, losing a
    // page is not.
    const next = frontier.length ? frontier.slice().sort()[0] : undefined;
    if (!next || next <= since) {
      // No strictly-forward move available: either an entity reported
      // `hasMore` without a usable timestamp, or a whole page shares one
      // `updated_at`. Advancing anyway would skip records; not advancing
      // would spin. Stop, and leave the cursor for the next sync — the same
      // "do not move a cursor you cannot move honestly" rule as above.
      console.warn(
        '[sync] Pull cannot advance past %s without skipping records; leaving the cursor here.',
        since
      );
      break;
    }
    since = next;
  }

  if (!pullComplete && !pullError && rounds >= MAX_PULL_ROUNDS) {
    console.warn('[sync] Pull stopped after %d rounds with more data outstanding.', rounds);
  }

  // 3. Store sync cursor — server time only, and only for a pull that finished.
  //
  // Two rules, both of which the old single `db.put` broke:
  //   (a) never the client clock. `result.timestamp` is a local wall clock and
  //       has no meaning as a `?since=` against the server's data.
  //   (b) never on a failed or partial pull. A cursor is a claim to have seen
  //       everything before it. We have not.
  if (pullComplete && serverTime) {
    await db.put('syncMeta', serverTime, 'lastSyncAt');
    result.watermark = serverTime;
  } else if (pullComplete) {
    // Pull finished but the server sent no `serverTime`. There is nothing
    // authoritative to write, so the cursor stays where it is — the next sync
    // re-reads a window it already has, which costs bandwidth and loses
    // nothing.
    console.warn('[sync] Pull completed without a serverTime; leaving the cursor at %s.', lastSyncAt);
  }

  // 4. Cleanup old queue entries — housekeeping, independent of the pull, and
  // worth doing even on the failure path below.
  await cleanupQueue();

  if (!pullComplete) {
    throw pullFailed(
      pullError
        ? `Sync pull failed: ${(pullError as any)?.message || pullError}`
        : 'Sync pull did not complete.',
      pullError
    );
  }

  return result;
}
